import { Book, BookFilters, BookStatus } from '../types/Book';

export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const getStatusColor = (status: BookStatus): string => {
  switch (status) {
    case BookStatus.WANT_TO_READ:
      return '#6366f1'; // Indigo
    case BookStatus.CURRENTLY_READING:
      return '#f59e0b'; // Amber
    case BookStatus.READ:
      return '#10b981'; // Emerald
    case BookStatus.DNF:
      return '#ef4444'; // Red
    default:
      return '#6b7280'; // Gray
  }
};

export const getStatusLabel = (status: BookStatus): string => {
  switch (status) {
    case BookStatus.WANT_TO_READ:
      return 'Want to Read';
    case BookStatus.CURRENTLY_READING:
      return 'Currently Reading';
    case BookStatus.READ:
      return 'Read';
    case BookStatus.DNF:
      return 'Did Not Finish';
    default:
      return status;
  }
};

export const filterBooks = (books: Book[], filters: BookFilters): Book[] => {
  return books.filter(book => {
    if (filters.status && book.status !== filters.status) {
      return false;
    }
    
    if (filters.genre) {
      // Check both genre field and tags array for subject matching
      const hasGenreMatch = book.genre === filters.genre;
      const hasTagMatch = book.tags && book.tags.includes(filters.genre);
      if (!hasGenreMatch && !hasTagMatch) {
        return false;
      }
    }
    
    if (filters.rating && book.rating !== filters.rating) {
      return false;
    }
    
    if (filters.position_slot && book.position_slot !== filters.position_slot) {
      return false;
    }
    
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      return (
        book.title.toLowerCase().includes(query) ||
        book.author.toLowerCase().includes(query) ||
        book.genre.toLowerCase().includes(query) ||
        (book.position_slot && book.position_slot.toLowerCase().includes(query)) ||
        (book.tags && book.tags.some(tag => tag.toLowerCase().includes(query))) ||
        (book.notes && book.notes.toLowerCase().includes(query))
      );
    }
    
    return true;
  });
};

export const sortBooks = (
  books: Book[], 
  sortBy: 'title' | 'author' | 'dateAdded' | 'rating' | 'position_slot' | 'publicationDate' = 'dateAdded',
  sortOrder: 'asc' | 'desc' = 'desc'
): Book[] => {
  return [...books].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'author':
        comparison = a.author.localeCompare(b.author);
        break;
      case 'rating':
        comparison = (a.rating || 0) - (b.rating || 0);
        break;
      case 'position_slot':
        const slotA = a.position_slot || '';
        const slotB = b.position_slot || '';
        // Sort empty slots last
        if (!slotA && slotB) return 1;
        if (slotA && !slotB) return -1;
        comparison = slotA.localeCompare(slotB);
        break;
      case 'publicationDate':
        // Handle publication date sorting
        const dateA = a.publicationDate ? new Date(a.publicationDate).getTime() : 0;
        const dateB = b.publicationDate ? new Date(b.publicationDate).getTime() : 0;
        // Books without publication date go to the end
        if (dateA === 0 && dateB !== 0) return 1;
        if (dateA !== 0 && dateB === 0) return -1;
        comparison = dateA - dateB;
        break;
      case 'dateAdded':
      default:
        comparison = new Date(a.dateAdded).getTime() - new Date(b.dateAdded).getTime();
        break;
    }
    
    // Apply sort order
    return sortOrder === 'desc' ? -comparison : comparison;
  });
};